import React from 'react';
import { Document, Page, Text, View, StyleSheet, PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';

interface FieldPosition {
  id: string;
  fieldName: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  textAlign: 'left' | 'center' | 'right';
  color: string;
}

interface CredentialTemplate {
  id: string;
  name: string;
  width: number;
  height: number;
  backgroundImage?: string;
  backgroundColor: string;
  fields: FieldPosition[];
}

interface CredentialData {
  id: string;
  title: string;
  issuer: string;
  issuerLogo: string;
  issuedDate: string;
  expiryDate: string | null;
  status: string;
  category: string;
  fields: Record<string, string>;
}

interface CredentialPDFProps {
  credential: CredentialData;
  template: CredentialTemplate;
  issuerInfo?: {
    name: string;
    address: string;
    website: string;
    logo?: string;
  };
}

const CredentialPDFDocument: React.FC<CredentialPDFProps> = ({ credential, template, issuerInfo }) => {
  const styles = StyleSheet.create({
    page: {
      backgroundColor: template.backgroundColor,
      padding: 40,
      fontFamily: 'Helvetica',
    },
    container: {
      width: template.width,
      height: template.height,
      position: 'relative',
      border: '2pt solid #253858',
      backgroundColor: template.backgroundColor,
    },
    header: {
      textAlign: 'center',
      marginBottom: 30,
    },
    title: {
      fontSize: 24,
      fontWeight: 'bold',
      color: '#253858',
      marginBottom: 10,
    },
    issuerName: {
      fontSize: 16,
      color: '#64748B',
      marginBottom: 20,
    },
    decorativeBorder: {
      position: 'absolute',
      top: 10,
      left: 10,
      right: 10,
      bottom: 10,
      border: '1pt solid #3B82F6',
    },
    watermark: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%) rotate(-45deg)',
      fontSize: 48,
      color: '#E2E8F0',
      opacity: 0.1,
      fontWeight: 'bold',
    },
    footer: {
      position: 'absolute',
      bottom: 20,
      left: 20,
      right: 20,
      textAlign: 'center',
      fontSize: 10,
      color: '#64748B',
    },
    verificationInfo: {
      marginTop: 10,
      fontSize: 8,
      color: '#64748B',
    },
    logo: {
      width: 60,
      height: 60,
      marginBottom: 15,
    }
  });

  return (
    <Document>
      <Page size={[template.width, template.height]} style={styles.page}>
        <View style={styles.container}>
          {/* Decorative border */}
          <View style={styles.decorativeBorder} />
          
          {/* Watermark */}
          <Text style={styles.watermark}>VERIFIED</Text>

          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>{credential.title}</Text>
            <Text style={styles.issuerName}>{credential.issuer}</Text>
          </View>

          {/* Dynamic fields based on template */}
          {template.fields.map((field) => {
            const fieldValue = credential.fields[field.fieldName] || field.fieldName;
            
            const fieldStyle = {
              position: 'absolute' as const,
              left: field.x,
              top: field.y,
              width: field.width,
              height: field.height,
              fontSize: field.fontSize,
              fontWeight: field.fontWeight === 'bold' ? 'bold' : 'normal' as const,
              textAlign: field.textAlign as 'left' | 'center' | 'right',
              color: field.color,
              display: 'flex',
              alignItems: 'center',
            };

            return (
              <View key={field.id} style={fieldStyle}>
                <Text>{fieldValue}</Text>
              </View>
            );
          })}

          {/* Footer with verification info */}
          <View style={styles.footer}>
            <Text>Credential ID: {credential.id}</Text>
            <Text>Issued: {credential.issuedDate}</Text>
            {credential.expiryDate && (
              <Text>Expires: {credential.expiryDate}</Text>
            )}
            <View style={styles.verificationInfo}>
              <Text>Verify this credential at: https://credentialcapital.co/verify/{credential.id}</Text>
              {issuerInfo && (
                <Text>{issuerInfo.name} • {issuerInfo.website}</Text>
              )}
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

interface CredentialPDFViewerProps extends CredentialPDFProps {
  mode?: 'viewer' | 'download';
  fileName?: string;
}

export default function CredentialPDF({ 
  credential, 
  template, 
  issuerInfo, 
  mode = 'viewer',
  fileName 
}: CredentialPDFViewerProps) {
  const defaultFileName = `${credential.title.replace(/\s+/g, '_')}_${credential.id}.pdf`;

  if (mode === 'download') {
    return (
      <PDFDownloadLink
        document={<CredentialPDFDocument credential={credential} template={template} issuerInfo={issuerInfo} />}
        fileName={fileName || defaultFileName}
        className="inline-flex items-center px-4 py-2 bg-electric-steel text-white rounded-lg hover:bg-blue-600 transition-colors"
      >
        {({ loading }) => (loading ? 'Generating PDF...' : 'Download PDF')}
      </PDFDownloadLink>
    );
  }

  return (
    <div className="w-full h-96 border border-gray-300 rounded-lg overflow-hidden">
      <PDFViewer width="100%" height="100%">
        <CredentialPDFDocument credential={credential} template={template} issuerInfo={issuerInfo} />
      </PDFViewer>
    </div>
  );
}

// Full-size PDF preview for modal dialogs (unchanged)
export function CredentialPreview({ credential, template }: CredentialPDFProps) {
  return (
    <div className="relative border-2 border-gray-300 rounded-lg overflow-hidden bg-white shadow-lg">
      <div
        className="relative mx-auto"
        style={{
          width: `${Math.min(template.width * 0.6, 480)}px`,
          height: `${Math.min(template.height * 0.6, 360)}px`,
          backgroundColor: template.backgroundColor,
        }}
      >
        {/* Decorative border */}
        <div className="absolute inset-2 border-2 border-midnight-indigo rounded"></div>
        
        {/* Watermark */}
        <div 
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ transform: 'rotate(-45deg)' }}
        >
          <span className="text-6xl font-bold text-gray-200 opacity-30">VERIFIED</span>
        </div>

        {/* Header */}
        <div className="absolute top-8 left-0 right-0 text-center">
          <h2 className="text-lg font-bold text-midnight-indigo mb-2">
            {credential.title}
          </h2>
          <p className="text-sm text-warm-slate">
            {credential.issuer}
          </p>
        </div>

        {/* Dynamic fields based on template */}
        {template.fields.map((field) => {
          const fieldValue = credential.fields[field.fieldName] || `[${field.fieldName}]`;
          
          return (
            <div
              key={field.id}
              className="absolute flex items-center"
              style={{
                left: `${field.x * 0.6}px`,
                top: `${field.y * 0.6}px`,
                width: `${field.width * 0.6}px`,
                height: `${field.height * 0.6}px`,
                fontSize: `${field.fontSize * 0.6}px`,
                fontWeight: field.fontWeight,
                textAlign: field.textAlign,
                color: field.color,
              }}
            >
              <span className="truncate">{fieldValue}</span>
            </div>
          );
        })}

        {/* Footer */}
        <div className="absolute bottom-4 left-4 right-4 text-center text-xs text-warm-slate">
          <p>Credential ID: {credential.id}</p>
          <p>Issued: {credential.issuedDate}</p>
          {credential.expiryDate && <p>Expires: {credential.expiryDate}</p>}
          <p className="mt-1 text-xs">
            Verify at: verify.credentialcapital.com/credential/{credential.id}
          </p>
        </div>
      </div>
    </div>
  );
}

// Small thumbnail preview for card layouts
export function CredentialThumbnail({ credential, template }: CredentialPDFProps) {
  return (
    <div className="relative w-full h-24 border border-gray-200 rounded-md overflow-hidden bg-white">
      <div
        className="relative w-full h-full"
        style={{
          backgroundColor: template.backgroundColor,
        }}
      >
        {/* Decorative border */}
        <div className="absolute inset-1 border border-midnight-indigo rounded opacity-80"></div>
        
        {/* Watermark */}
        <div 
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ transform: 'rotate(-45deg)' }}
        >
          <span className="text-2xl font-bold text-gray-200 opacity-20">VERIFIED</span>
        </div>

        {/* Simplified header for thumbnail */}
        <div className="absolute top-2 left-2 right-2 text-center">
          <h3 className="text-xs font-bold text-midnight-indigo truncate">
            {credential.title}
          </h3>
          <p className="text-xs text-warm-slate truncate">
            {credential.issuer}
          </p>
        </div>

        {/* Simplified field display - only show key fields */}
        <div className="absolute bottom-2 left-2 right-2">
          <div className="flex justify-between items-center text-xs">
            <span className="text-warm-slate truncate flex-1">
              {Object.values(credential.fields)[0] || 'Certificate Holder'}
            </span>
            <span className="text-xs text-warm-slate ml-2">
              {credential.issuedDate.split('-')[0]}
            </span>
          </div>
        </div>

        {/* Category indicator */}
        <div className="absolute top-1 right-1">
          <div className="w-2 h-2 rounded-full bg-electric-steel"></div>
        </div>
      </div>
    </div>
  );
}